import { useQuery } from '@tanstack/react-query';

import { NotFoundError } from '../shared/api/client';
import { QUERY_STALE_TIMES } from '../../shared/providers/react-query-provider';
import { AvailableLocale } from '../../shared/intl/types';

import {
    fetchDossierAppointments,
    getAgentScheduleLink,
    getAvailabilityConfig,
    getCalendarStatus,
} from './api';
import { AgentScheduleLink, Appointment, AvailabilityConfig, CalendarStatus } from './types';

export const getCalendarStatusQueryKey = () => ['calendarConfig', 'status'] as const;

export const useGetCalendarStatus = () =>
    useQuery<CalendarStatus>({
        queryKey: getCalendarStatusQueryKey(),
        queryFn: getCalendarStatus,
        staleTime: QUERY_STALE_TIMES.AGENT_SETTINGS,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

export const getAvailabilityConfigQueryKey = () => ['appointment', 'availability-config'] as const;

export const useGetAvailabilityConfig = () =>
    useQuery<AvailabilityConfig>({
        queryKey: getAvailabilityConfigQueryKey(),
        queryFn: getAvailabilityConfig,
        staleTime: QUERY_STALE_TIMES.AGENT_SETTINGS,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => failureCount < 3 && !(error instanceof NotFoundError),
    });

export const getDossierAppointmentsQueryKey = (resourceId: string) =>
    ['appointment', 'dossier', resourceId] as const;

export const useGetDossierAppointments = (resourceId: string) =>
    useQuery<Appointment[]>({
        queryKey: getDossierAppointmentsQueryKey(resourceId),
        queryFn: () => fetchDossierAppointments(resourceId),
        staleTime: QUERY_STALE_TIMES.DOSSIER,
    });

export const getAgentScheduleLinkQueryKey = (locale: AvailableLocale) =>
    ['appointment', 'schedule-link', locale] as const;

export const useGetAgentScheduleLink = (locale: AvailableLocale, enabled = true) =>
    useQuery<AgentScheduleLink>({
        queryKey: getAgentScheduleLinkQueryKey(locale),
        queryFn: () => getAgentScheduleLink(locale),
        staleTime: QUERY_STALE_TIMES.AGENT_SETTINGS,
        refetchOnWindowFocus: false,
        enabled,
    });
